import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Input, Button } from '@components';
import { caldavService } from '../services/caldav';
import { calendarIntegrationService } from '../services/calendarIntegration';

interface CalDavConnectionFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  provider: 'apple' | 'caldav';
}

export const CalDavConnectionForm: React.FC<CalDavConnectionFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  provider,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<'credentials' | 'select'>('credentials');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [calendarId, setCalendarId] = useState<string | null>(null);
  const [calendars, setCalendars] = useState<Array<{ name: string; url: string }>>([]);
  const [selectedUrl, setSelectedUrl] = useState<string>('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [caldavBaseUrl, setCaldavBaseUrl] = useState('');
  const [calendarUrl, setCalendarUrl] = useState('');

  const isApple = provider === 'apple';

  const resetForm = () => {
    setStep('credentials');
    setError(null);
    setEmail('');
    setPassword('');
    setCaldavBaseUrl('');
    setCalendarUrl('');
    setSelectedUrl('');
    setCalendarId(null);
    setCalendars([]);
  };

  const handleSubmit = async () => {
    if (!email || !password) {
      setError(t('calendars.emailPasswordRequired'));
      return;
    }

    if (!caldavBaseUrl) {
      setError(t('calendars.caldavBaseUrlRequired'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const created = await calendarIntegrationService.create({
        provider: isApple ? 'apple' : 'caldav',
      });

      const startDate = new Date();
      startDate.setFullYear(startDate.getFullYear() - 1);
      const start = startDate.toISOString();

      const connectRes = await caldavService.connect({
        calendar_id: created.id,
        email,
        password,
        start,
        ...(caldavBaseUrl && { caldav_base_url: caldavBaseUrl }),
        ...(calendarUrl && { calendar_url: calendarUrl }),
      });

      setCalendarId(created.id);
      setCalendars(connectRes.calendars);
      setStep('select');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('calendars.failedToConnectCalDAV'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!calendarId || !selectedUrl) return;

    try {
      setLoading(true);
      setError(null);

      await caldavService.confirm({
        calendar_id: calendarId,
        calendar_url: selectedUrl,
      });

      resetForm();
      onSuccess();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('calendars.failedToConfirmCalendarSelection'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    resetForm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="" size="md" showCloseButton={!loading}>
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {isApple ? t('calendars.connectAppleCalDAV') : t('calendars.connectCalDAV')}
        </h3>

        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded">
            {error}
          </div>
        )}

        {step === 'credentials' && (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isApple
                ? t('calendars.appleAppSpecificPasswordDescription')
                : t('calendars.caldavCredentialsDescription')}
            </p>

            <div className="space-y-3">
              <div>
                <label
                  htmlFor="caldav-email"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  {isApple ? t('calendars.appleIdEmail') : t('calendars.email')}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <Input
                  id="caldav-email"
                  placeholder={
                    isApple
                      ? t('calendars.appleIdEmailPlaceholder')
                      : t('calendars.emailAddressPlaceholder')
                  }
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  type="email"
                  disabled={loading}
                />
              </div>

              <div>
                <label
                  htmlFor="caldav-password"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  {isApple ? t('calendars.appSpecificPassword') : t('calendars.password')}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <Input
                  id="caldav-password"
                  placeholder={
                    isApple
                      ? t('calendars.appSpecificPasswordPlaceholder')
                      : t('calendars.passwordPlaceholder')
                  }
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div>
                <label
                  htmlFor="caldav-base-url"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  {t('calendars.caldavBaseUrl')} <span className="text-red-500">*</span>
                </label>
                <Input
                  id="caldav-base-url"
                  placeholder={
                    isApple ? 'https://caldav.icloud.com' : 'https://webmail.example.com'
                  }
                  value={caldavBaseUrl}
                  onChange={e => setCaldavBaseUrl(e.target.value)}
                  type="url"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('calendars.caldavBaseUrlHelper')}
                </p>
              </div>

              <div>
                <label
                  htmlFor="caldav-calendar-url"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  {t('calendars.calendarUrlOptional')}
                </label>
                <Input
                  id="caldav-calendar-url"
                  placeholder="https://webmail.example.com/rpc.php/calendars/user/calendar/"
                  value={calendarUrl}
                  onChange={e => setCalendarUrl(e.target.value)}
                  type="url"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('calendars.calendarUrlHelper')}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="light" onClick={handleClose} disabled={loading}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="gradient-multi"
                onClick={handleSubmit}
                loading={loading}
                disabled={!email || !password || !caldavBaseUrl || loading}
              >
                {t('common.continue')}
              </Button>
            </div>
          </>
        )}

        {step === 'select' && (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('calendars.selectCalendarToConnect')}
            </p>

            {calendars.length === 0 ? (
              <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                {t('calendars.noCalendarsFound')}
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-auto">
                {calendars
                  .filter(opt => !opt.name.toLowerCase().startsWith('reminders'))
                  .map(opt => (
                    <label
                      key={opt.url}
                      className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
                    >
                      <input
                        type="radio"
                        name="caldavCalendar"
                        checked={selectedUrl === opt.url}
                        onChange={() => setSelectedUrl(opt.url)}
                        className="text-blue-600"
                        disabled={loading}
                      />
                      <span className="text-sm text-gray-900 dark:text-white flex-1">
                        {opt.name}
                      </span>
                    </label>
                  ))}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="light" onClick={handleClose} disabled={loading}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="gradient-multi"
                onClick={handleConfirm}
                loading={loading}
                disabled={!selectedUrl || loading}
              >
                {t('calendars.connectCalendar')}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
